/**
 * Phase 6a.16 / 6a.20 — Decimal-safe addition of currency strings.
 *
 * Currency amounts arrive from the backend as 2-decimal strings
 * (`"1234.56"`, `"-200.00"`). Naively summing with `Number` accumulates
 * floating-point drift; for 2-decimal currency the drift only matters
 * at the rounding boundary, but for an audit deliverable any
 * sub-cent miscount is unacceptable.
 *
 * Strategy: split each value on `.`, treat both sides as integer
 * cents via `BigInt`, sum exactly, then format back to a 2-decimal
 * string. Re-extracted from `PajesTab` so the Excel export pipeline
 * can import it without dragging React into the dynamic chunk.
 *
 *   • Empty / blank values → contribute 0.
 *   • Negative values supported (leading `-`).
 *   • Fractions longer than 2 digits → rounded half-up to 2 decimals on
 *     the value's own magnitude (defensive — backend already emits
 *     2-decimal currency strings; inputs with ≤2 fraction digits are
 *     unaffected and produce byte-identical results). Rounding the
 *     sub-cent BEFORE summing keeps "sum of rows = displayed total":
 *     `"0.755"` contributes `0.76`, never a silently-dropped `0.75`.
 *   • Missing fraction part → treated as `.00`.
 */
export function sumDecimalStrings(values: string[]): string {
  let totalCents = 0n;
  for (const raw of values) {
    const trimmed = (raw ?? "0").trim();
    if (!trimmed) continue;
    const negative = trimmed.startsWith("-");
    const body = negative ? trimmed.slice(1) : trimmed;
    const [intPart = "0", fracRaw = ""] = body.split(".");
    // First two fraction digits are the cents (unchanged path for the
    // ≤2-decimal common case). A third digit, if present, drives a
    // half-up round so sub-cent precision is rounded into the cents
    // rather than silently truncated.
    const frac = (fracRaw + "00").slice(0, 2);
    let cents = BigInt(intPart) * 100n + BigInt(frac || "0");
    if (fracRaw.length > 2 && fracRaw.charCodeAt(2) >= 53 /* '5' */) {
      cents += 1n;
    }
    totalCents += negative ? -cents : cents;
  }
  const isNeg = totalCents < 0n;
  const abs = isNeg ? -totalCents : totalCents;
  const intStr = (abs / 100n).toString();
  const fracStr = (abs % 100n).toString().padStart(2, "0");
  return `${isNeg ? "-" : ""}${intStr}.${fracStr}`;
}
